import { useTranslation } from "@sagot/i18n";
import { useEffect, useMemo, useRef } from "react";
import { useMembers } from "../../../roles/use-roles";
import { useFormatters } from "../shared/format";
import type { ConversationDetail, TimelineView } from "./inbox-model";
import { HandoffCard } from "./handoff-card";
import { AssistantBubble, NoteCard, StaffBubble, SystemDivider, UserBubble } from "./message-bubbles";
import { snoozeWhen } from "./snooze";
import { buildTimeline, type TimelineEntry } from "./timeline";

type Translate = ReturnType<typeof useTranslation>["t"];

const str = (value: unknown): string | null => (typeof value === "string" && value ? value : null);

interface TimelineFormat {
  time: (iso: string) => string;
  dateTime: (iso: string) => string;
  timeZone: string;
}

/** "Snoozed" / "Snooze ended" rows: who or what snoozed it, until when, and why it woke. */
function snoozeEventText(event: TimelineView, t: Translate, fmt: TimelineFormat, memberName: (id: string | null) => string | null): string {
  const time = fmt.time(event.created_at);
  const data = event.data;
  if (event.type === "snoozed") {
    const until = str(data.until);
    const when = until ? snoozeWhen(until, new Date(event.created_at).getTime(), fmt) : "";
    const workflow = str(data.workflow);
    if (!until) return t("engage.inbox.events.snoozedNoTime", { time });
    return workflow ? t("engage.inbox.events.snoozed", { workflow, when, time }) : t("engage.inbox.events.snoozedPlain", { when, time });
  }
  const reason = str(data.reason) ?? "timer";
  if (reason === "staff") return t("engage.inbox.events.unsnoozedBy", { name: memberName(event.actor_id) ?? t("engage.inbox.events.someone"), time });
  return t(`engage.inbox.events.unsnoozed.${reason}`, { defaultValue: t("engage.inbox.events.unsnoozed.timer"), time });
}

function eventText(event: TimelineView, t: Translate, fmt: TimelineFormat, memberName: (id: string | null) => string | null): string {
  const data = event.data;
  const time = fmt.time(event.created_at);
  switch (event.type) {
    case "snoozed":
    case "unsnoozed":
      return snoozeEventText(event, t, fmt, memberName);
    case "handoff_requested": {
      const reason = str(data.reason);
      return reason ? t("engage.inbox.events.handoffReason", { reason: t(`engage.inbox.events.reasons.${reason}`, { defaultValue: reason }), time }) : t("engage.inbox.events.handoff_requested", { time });
    }
    case "assigned": {
      const name = memberName(str(data.assignee_user_id)) ?? str(data.assignee) ?? str(data.team);
      return name ? t("engage.inbox.events.assigned", { name, time }) : t("engage.inbox.events.unassigned", { time });
    }
    case "status_changed": {
      const to = str(data.to) ?? "";
      return t("engage.inbox.events.status_changed", { status: t(`engage.inbox.status.${to}`, { defaultValue: to }), time });
    }
    case "returned_to_queue":
      return t("engage.inbox.events.returned_to_queue", { time });
    case "takeover":
      return t("engage.inbox.events.takeover", { name: str(data.user_name) ?? t("engage.inbox.events.someone"), time });
    default:
      return t(`engage.inbox.events.${event.type}`, { time });
  }
}

function Entry({ entry, visitor, t, memberName }: { entry: TimelineEntry; visitor: string; t: Translate; memberName: (id: string | null) => string | null }) {
  const fmt = useFormatters();
  if (entry.kind === "day") {
    const sameDay = (iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: fmt.timeZone }).format(new Date(iso));
    const date = fmt.date(entry.at);
    const isToday = sameDay(entry.at) === sameDay(new Date().toISOString());
    return <div className="text-center text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">{isToday ? t("engage.inbox.thread.today", { date }) : date}</div>;
  }
  if (entry.kind === "event") {
    return (
      <SystemDivider icon={entry.icon} tone={entry.tone}>
        {eventText(entry.event, t, fmt, memberName)}
      </SystemDivider>
    );
  }
  const { message } = entry;
  switch (message.role) {
    case "user":
      return <UserBubble message={message} name={visitor} />;
    case "assistant":
      return <AssistantBubble message={message} />;
    case "staff":
      return <StaffBubble message={message} />;
    case "note":
      return <NoteCard message={message} />;
    default:
      return (
        <SystemDivider icon="info" tone="muted">
          {message.content} · {fmt.time(message.created_at)}
        </SystemDivider>
      );
  }
}

/** Chronological thread: bubbles, notes, system events and day separators; sticks to the bottom. */
export function ThreadMessages({ detail }: { detail: ConversationDetail }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const members = useMembers();
  const scroller = useRef<HTMLDivElement>(null);
  const dayFmt = useMemo(() => new Intl.DateTimeFormat("en-CA", { timeZone: fmt.timeZone }), [fmt.timeZone]);
  const entries = useMemo(() => buildTimeline(detail.messages, detail.events, (iso) => dayFmt.format(new Date(iso))), [detail.messages, detail.events, dayFmt]);
  const memberName = (id: string | null) => (id ? (members.data?.find((m) => m.user_id === id)?.name ?? null) : null);
  const last = entries.at(-1)?.id;

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [last, detail.conversation.id]);

  return (
    <div
      ref={scroller}
      role="log"
      aria-live="polite"
      aria-label={t("engage.inbox.thread.messagesLabel")}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- the scrollable thread must be keyboard-scrollable (axe scrollable-region-focusable)
      tabIndex={0}
      className="flex-1 space-y-4 overflow-y-auto px-4 py-5 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary lg:px-8"
      data-testid="thread-messages"
    >
      <HandoffCard detail={detail} />
      {entries.map((entry) => (
        <Entry key={entry.id} entry={entry} visitor={detail.conversation.visitor_name} t={t} memberName={memberName} />
      ))}
    </div>
  );
}
